import { DEFAULT_ENVIRONMENT_IMAGE_URL } from '../../core/configdata';

export const ENVIRONMENT_PRESET_NAMES = ['none', 'neutral', 'studio', 'country', 'night', 'parking', 'forest'] as const;
export type EnvironmentPreset = typeof ENVIRONMENT_PRESET_NAMES[number];
export type EnvironmentPresetUrls = Readonly<Partial<Record<EnvironmentPreset, string>>>;

export function presetIsBenchSafe(preset: EnvironmentPreset): boolean {
    return preset === 'none' || preset === 'neutral';
}

export const VIEWER_DEFAULT_KEY_LIGHT = {
    intensity: 3.0,
    color: '#FFF5D6',
    direction: [-0.5, -0.766, -0.406] as [number, number, number],
    elevation: 50,
    azimuth: 51,
} as const;

export interface SpatialLightOptions {
    intensity?: number;
    color?: string;
    elevation?: number;
    azimuth?: number;
}

export interface ResolvedKeyLight {
    intensity: number;
    color: string;
    elevation: number;
    azimuth: number;
    direction: [number, number, number];
}

export interface SpatialEnvironmentDescriptor {
    preset: EnvironmentPreset;
    environmentImage: string;
    intensity: number;
    rotation: number;
    background?: string;
    keyLight?: ResolvedKeyLight;
    benchSafe: boolean;
}

export interface SpatialEnvironmentOptions {
    intensity?: number;
    rotation?: number;
    background?: string;
}

const HEX = /^#[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/;

function requireFinite(value: unknown, label: string): number {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        throw new Error(`${label} must be a finite number.`);
    }
    return value;
}

function requireHex(value: unknown, label: string): string {
    if (typeof value !== 'string' || !HEX.test(value.trim())) {
        throw new Error(`${label} must be a hex colour, e.g. "#FFF5D6".`);
    }
    return value.trim().slice(0, 7).toUpperCase();
}

export function directionFromSky(elevation: number, azimuth: number): [number, number, number] {
    const el = (elevation * Math.PI) / 180;
    const az = (azimuth * Math.PI) / 180;
    const horizontal = Math.cos(el);
    // `|| 0` collapses -0: cos(90°) is 1.6e-17, whose rounded negation is -0,
    // which survives a deep-equal against 0 as a difference and reads as a
    // sign error in a direction vector that has none.
    const round = (n: number): number => (Math.round(n * 1000) / 1000) || 0;
    // Position in the sky, negated: the light travels from there toward origin.
    return [
        round(-Math.sin(az) * horizontal),
        round(-Math.sin(el)),
        round(-Math.cos(az) * horizontal),
    ];
}

export function resolveKeyLight(options: unknown): ResolvedKeyLight {
    if (options === null || typeof options !== 'object' || Array.isArray(options)) {
        throw new Error('light(): expects an options object, e.g. { intensity: 4, elevation: 35, azimuth: -40 }.');
    }
    const bag = options as Record<string, unknown>;
    const allowed = ['intensity', 'color', 'elevation', 'azimuth'];
    const unknown = Object.keys(bag).filter(k => !allowed.includes(k));
    if (unknown.length > 0) {
        throw new Error(
            `light(): unknown option(s) ${unknown.map(k => `"${k}"`).join(', ')}. Allowed: ${allowed.join(', ')}.`,
        );
    }

    const intensity = bag.intensity === undefined
        ? VIEWER_DEFAULT_KEY_LIGHT.intensity
        : Math.max(0, requireFinite(bag.intensity, 'light(): intensity'));
    const color = bag.color === undefined
        ? VIEWER_DEFAULT_KEY_LIGHT.color
        : requireHex(bag.color, 'light(): color');
    const elevation = bag.elevation === undefined
        ? VIEWER_DEFAULT_KEY_LIGHT.elevation
        : Math.max(-90, Math.min(90, requireFinite(bag.elevation, 'light(): elevation')));
    const azimuth = bag.azimuth === undefined
        ? VIEWER_DEFAULT_KEY_LIGHT.azimuth
        : requireFinite(bag.azimuth, 'light(): azimuth');

    return { intensity, color, elevation, azimuth, direction: directionFromSky(elevation, azimuth) };
}

export function resolveEnvironment(preset: unknown, options?: unknown, urls: EnvironmentPresetUrls = {}): SpatialEnvironmentDescriptor {
    if (typeof preset !== 'string' || !(ENVIRONMENT_PRESET_NAMES as readonly string[]).includes(preset)) {
        throw new Error(
            `environment(): unknown preset ${JSON.stringify(preset)}. `
            + `Choose one of: ${ENVIRONMENT_PRESET_NAMES.join(', ')}.`,
        );
    }
    const name = preset as EnvironmentPreset;
    const environmentImage = name === 'none' ? '' : name === 'neutral' ? DEFAULT_ENVIRONMENT_IMAGE_URL : urls[name];
    if (name !== 'none' && (typeof environmentImage !== 'string' || !environmentImage)) {
        throw new Error(`environment(): the host has no URL for preset "${name}".`);
    }

    let intensity = 0.5;
    let rotation = 0;
    let background: string | undefined;
    if (options !== undefined) {
        if (options === null || typeof options !== 'object' || Array.isArray(options)) {
            throw new Error('environment(): second argument must be an options object, e.g. { intensity: 0.8 }.');
        }
        const bag = options as Record<string, unknown>;
        const allowed = ['intensity', 'rotation', 'background'];
        const unknown = Object.keys(bag).filter(k => !allowed.includes(k));
        if (unknown.length > 0) {
            throw new Error(
                `environment(): unknown option(s) ${unknown.map(k => `"${k}"`).join(', ')}. `
                + `Allowed: ${allowed.join(', ')}.`,
            );
        }
        if (bag.intensity !== undefined) {
            intensity = Math.max(0, Math.min(4, requireFinite(bag.intensity, 'environment(): intensity')));
        }
        if (bag.rotation !== undefined) rotation = requireFinite(bag.rotation, 'environment(): rotation');
        if (bag.background !== undefined) background = requireHex(bag.background, 'environment(): background');
    }

    return {
        preset: name,
        environmentImage: environmentImage ?? '',
        intensity,
        rotation,
        ...(background !== undefined ? { background } : {}),
        benchSafe: presetIsBenchSafe(name),
    };
}

export function describeEnvironment(env: SpatialEnvironmentDescriptor): string {
    const parts = [`environment "${env.preset}" (IBL ${env.intensity})`];
    if (env.background) parts.push(`background ${env.background}`);
    if (env.keyLight) {
        parts.push(
            `key light intensity ${env.keyLight.intensity} ${env.keyLight.color} `
            + `from ${env.keyLight.elevation}° elevation / ${env.keyLight.azimuth}° azimuth`,
        );
    }
    if (!env.benchSafe) {
        parts.push('NOTE: this preset does not resolve in the bench capture page (only "neutral"/"none" do)');
    }
    return parts.join('; ');
}
